import { useEffect, useCallback, useState, useRef } from "react";
import { useGetMemberByIdQuery } from "@/services/memberApi";
import {
  ForeeCheckoutObject,
  ForeePaymentToken,
  ForeeCallbackResponse,
} from "@/types";
import { decodeJwtToken } from "@/lib/helper";
import { useUpdateBookingStatusMutation } from "@/services/faciltiesApi";
declare global {
  interface Window {
    initiateForeeCheckout: (config: ForeeCheckoutObject) => void;
    handleForeeCallback: (response: ForeeCallbackResponse) => void;
  }
}

interface UseForeeCheckoutProps {
  token: string;
  onSuccess?: (response: ForeeCallbackResponse) => void;
  onError?: (error: string) => void;
  onReady?: () => void;
}

export const useForeeCheckout = ({
  token,
  onSuccess,
  onError,
  onReady,
}: UseForeeCheckoutProps) => {
  const [isReady, setIsReady] = useState(false);
  const [updateBookingStatus] = useUpdateBookingStatusMutation();
  const [decodedToken, setDecodedToken] = useState<ForeePaymentToken | null>(
    null
  );
  const hasCalledOnReadyRef = useRef(false);

  // Initial script verification
  // useEffect(() => {
  //   console.log('[Foree] Initial script verification:');
  //   console.log('[Foree] window.initiateForeeCheckout:', typeof window.initiateForeeCheckout);
  //   console.log('[Foree] Foree script element:', document.querySelector('script[src*="foree"]'));

  //   // Check if script is loaded in DOM
  //   const foreeScript = document.querySelector('script[src*="checkout-sandbox.foree.co"], script[src*="checkout.foree.co"]');
  //   if (!foreeScript) {
  //     console.warn('[Foree] Foree script not found in DOM');
  //   } else {
  //     console.log('[Foree] Foree script found:', foreeScript.getAttribute('src'));
  //   }
  // }, []);

  // Decode the token once
  useEffect(() => {
    if (!token) return;

    try {
      const decoded = decodeJwtToken(token) as ForeePaymentToken;
      setDecodedToken(decoded);
    } catch (error) {
      console.error("[Foree] Error decoding token:", error);
      if (onErrorRef.current) {
        onErrorRef.current("Invalid payment token");
      }
    }
  }, [token]);

  // Fetch member data using the user_id from token (which is the member_id)
  const {
    data: member,
    isLoading: isLoadingMember,
    error: memberError,
  } = useGetMemberByIdQuery(
    {
      org_id: decodedToken?.org_id || 0,
      auth_token: decodedToken?.authorization || "",
      id: decodedToken?.user_id || 0, // user_id is actually the member_id
    },
    {
      skip: !decodedToken?.user_id || !decodedToken?.org_id,
    }
  );

  // Store callbacks in refs to avoid dependency issues
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const onReadyRef = useRef(onReady);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    onReadyRef.current = onReady;
  }, [onSuccess, onError, onReady]);


  // Check readiness when dependencies change (no polling)
  useEffect(() => {
    console.log('[Foree] Checking readiness:', {
      hasScript: typeof window.initiateForeeCheckout === "function",
      hasMemberData: !!member,
      hasTokenData: !!decodedToken,
      isLoadingMember,
      isReady
    });

    // Don't recheck if already ready
    if (isReady) return;

    const hasScript = typeof window.initiateForeeCheckout === "function";
    const hasMemberData = !!member;
    const hasTokenData = !!decodedToken;
    const memberLoading = isLoadingMember;

    if (hasScript && !memberLoading && hasMemberData && hasTokenData) {
      console.log('[Foree] ✅ All requirements met, setting ready state');
      setIsReady(true);

      if (!hasCalledOnReadyRef.current && onReadyRef.current) {
        hasCalledOnReadyRef.current = true;
        onReadyRef.current();
      }
    } else {
      console.log('[Foree] ⏳ Still waiting for requirements:', {
        hasScript,
        hasMemberData,
        hasTokenData,
        memberLoading
      });
    }
  }, [decodedToken, member, isLoadingMember, isReady]);

  // Handle member fetch error
  useEffect(() => {
    if (memberError && onErrorRef.current) {
      console.error("[Foree] Failed to fetch member");
      onErrorRef.current("Failed to fetch member information");
    }
  }, [memberError]);

  // Foree callback function
  const handleForeeCallback = useCallback(
    async (response: ForeeCallbackResponse) => {
      console.log("handleForeeCallback", { response });
      if (response.status == 1) {
        if (decodedToken?.item_type == "booking") {
          await updateBookingStatus({
            booking_id: decodedToken?.item_id || 0,
            payment_action: "Processing",
            org_id: decodedToken?.org_id || 0,
            auth_token: decodedToken?.authorization || "",
          });
        }
        // Success
        if (onSuccessRef.current) {
          onSuccessRef.current(response);
        }
      } else {
        // Failure
        if (onErrorRef.current) {
          onErrorRef.current(response.message || "Payment failed");
        }
      }
    },
    []
  );

  // Initialize Foree checkout
  const initiateCheckout = useCallback(() => {
    if (!isReady || !decodedToken || !member) {
      if (onErrorRef.current) {
        onErrorRef.current("Payment initialization not ready");
      }
      return;
    }

    // Get Foree API key from environment variables
    const foreeApiKey = (import.meta as any).env?.VITE_FOREE_CHECKOUT_KEY || "";

    if (!foreeApiKey) {
      if (onErrorRef.current) {
        onErrorRef.current("Foree API key not configured");
      }
      return;
    }

    // Create checkout object according to Foree documentation
    const checkoutObject: ForeeCheckoutObject = {
      key: foreeApiKey,
      amount: String(decodedToken.amount), // Convert to string as required by Foree
      create_bill: true, //required for online billers
      reference_number: String(decodedToken.invoice_id), // Use invoice_id as reference_number
      callback: handleForeeCallback, // Function name as string
      // callback_url: `${window.location.origin}/payment/process?t=${encodeURIComponent(token)}`,
      customer_email_address: member.email || "",
      customer_phone_number: member.phone || member.mobile_number || "",
      consumer_name: `${member.first_name || ""} ${
        member.last_name || ""
      }`.trim(),
      payment_method: "", // Optional, leave empty for user selection
      bill_details_id: "", // Optional
      token: String(decodedToken?.authorization), // Add token as per docs
    };

    console.log({checkoutObject})
    try {
      // Verify script is loaded
      if (typeof window.initiateForeeCheckout !== "function") {
        console.error("[Foree] initiateForeeCheckout function not available");
        if (onErrorRef.current) {
          onErrorRef.current("Foree checkout script not loaded properly");
        }
        return;
      }

      // Make callback function globally available
      window.handleForeeCallback = handleForeeCallback;

      window.initiateForeeCheckout(checkoutObject);
    } catch (error) {
      console.error("[Foree] Error initiating checkout:", error);
      if (onErrorRef.current) {
        onErrorRef.current(`Failed to initiate payment: ${error}`);
      }
    }
  }, [isReady, decodedToken, member, token, handleForeeCallback]);

  return {
    isReady,
    isLoadingMember,
    decodedToken,
    memberData: member,
    initiateCheckout,
    handleForeeCallback,
  };
};

export default useForeeCheckout;
